/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { isEmpty } from 'lodash';
import Image from 'next/image';
import Link from 'next/link';
import posthog from 'posthog-js';
import { useMemo } from 'react';

import { SEO } from '../components/atoms/SEO';
import { DescriptionModal } from '../components/molecule/DescriptionModal';
import { PageTitle } from '../components/molecule/PageTitle';
import { SectionTitle } from '../components/molecule/SectionTitle';
import { useI18n } from '../i18n/useI18n';
import { isBefore } from '../services/isBefore';
import { parseDiplomaDate, parseEndDate, parseStartDate } from '../services/staticData/parseDateFields';
import { HTMLStyleText } from '../services/textUtils/HTMLStyleText';
import diplomasData from '../static_data/diplomas.json';
import experiencesData from '../static_data/experiences.json';

export async function getStaticProps() {
  return {
    props: {
      exps: experiencesData,
      educations: diplomasData,
    },
    // revalidate: 60 * 60 * 24, // 24 hours
  };
}

const About = ({
  exps,
  educations,
}: {
  exps: {
    Company: string;
    'Job Title EN': string;
    'Description EN': string;
    'Job Title FR': string;
    'Description FR': string;
    'Date From': string | { start: string };
    'Date To': null | string | { start: string };
  }[];
  educations: {
    Company: string;
    'Title EN': string;
    'Description EN': string;
    'Title FR': string;
    'Description FR': string;
    Date: string | { start: string };
  }[];
}) => {
  const { t, format, actualLang } = useI18n();

  const experiences = useMemo(
    () =>
      (exps || [])
        .map((exp) => ({
          jobTitle: actualLang === 'fr' ? exp['Job Title FR'] : exp['Job Title EN'],
          description: actualLang === 'fr' ? exp['Description FR'] : exp['Description EN'],
          dateFrom: parseStartDate(exp['Date From']),
          dateTo: parseEndDate(exp['Date To']),
          company: exp.Company,
        }))
        .sort((a, b) => (isBefore(a.dateFrom, b.dateFrom) ? 1 : -1)),
    [actualLang, exps],
  );

  const diplomas = useMemo(
    () =>
      (educations || [])
        .map((diploma) => ({
          name: actualLang === 'fr' ? diploma['Title FR'] : diploma['Title EN'],
          description: actualLang === 'fr' ? diploma['Description FR'] : diploma['Description EN'],
          date: parseDiplomaDate(diploma.Date),
          company: diploma.Company,
        }))
        .sort((a, b) => (isBefore(a.date, b.date) ? 1 : -1)),
    [actualLang, exps],
  );

  return (
    <>
      <SEO title={t('pages.about.title')!} description={t('pages.about.description')!} />
      <div>
        <PageTitle title={t('pages.about.title')!} description={t('pages.about.description')!} />

        <div className="divide-y divide-dotted divide-gray-500/50">
          <div className="mb-12 space-y-6">
            <p dangerouslySetInnerHTML={{ __html: HTMLStyleText(t('pages.about.presentation.1')!) }}></p>

            <p
              dangerouslySetInnerHTML={{
                __html: HTMLStyleText(
                  t('pages.about.presentation.2', {
                    jobTitle: experiences?.length > 0 ? experiences[0].jobTitle : '',
                    company: experiences?.length > 0 ? experiences[0].company : '',
                  })!,
                ),
              }}
            ></p>

            <p dangerouslySetInnerHTML={{ __html: HTMLStyleText(t('pages.about.presentation.3')!) }}></p>

            <div className="grid grid-cols-1 items-center gap-y-5 md:grid-cols-4 md:gap-x-5">
              <div className="order-2 col-span-1 mx-auto md:order-1">
                <Image
                  src="/imgs/qlaffont.jpg"
                  height="150"
                  width="150"
                  className="rounded-xl"
                  alt="Quentin Laffont profile"
                />
              </div>
              <div className="order-1 col-span-3 space-y-3 md:order-2">
                <p dangerouslySetInnerHTML={{ __html: HTMLStyleText(t('pages.about.presentation.4')!) }}></p>
              </div>
            </div>

            <div className="text-center">
              <Link
                passHref
                href="/cv"
                target="_blank"
                rel="noreferrer"
                className="mx-auto flex w-max items-center gap-1 rounded-full border bg-gray-200 px-6 py-3 font-medium shadow-lg shadow-gray-200 hover:opacity-70 dark:bg-[#1c1b22] dark:text-white dark:shadow-gray-200/20"
                onClick={() => posthog.capture('cv_downloaded')}
              >
                <i className="icon icon-download h-5 w-5 bg-black dark:bg-white"></i>
                <p>{t('pages.about.cv')}</p>
              </Link>
            </div>
          </div>

          <div className="mb-12 space-y-5 pt-12">
            <SectionTitle title={t('pages.about.experiences')!} />

            <div className="space-y-3 pt-5">
              {experiences.map((experience, index) => (
                <div className="flex flex-wrap items-center justify-between gap-3" key={index}>
                  <p className="font-semibold">
                    {experience.jobTitle} - <span className="border-b border-sky-500">{experience.company}</span>
                  </p>
                  <span className="hidden grow border-t border-dashed border-gray-300 dark:border-gray-700 md:block"></span>
                  <p className="text-gray-500">
                    {format(experience.dateFrom, 'MMM yyyy')}
                    {experience.dateTo && ` - ${format(experience.dateTo!, 'MMM yyyy')}`}
                    {!isEmpty(experience.description) && <DescriptionModal description={experience.description} />}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-12 space-y-5 pt-12">
            <SectionTitle title={t('pages.about.diplomas')!} />

            <div className="space-y-3 pt-5">
              {diplomas.map((diploma, index) => (
                <div className="flex flex-wrap items-center justify-between gap-3" key={index}>
                  <p className="font-semibold">
                    {diploma.name} - <span className="border-b border-sky-500">{diploma.name}</span>
                  </p>
                  <span className="hidden grow border-t border-dashed border-gray-300 dark:border-gray-700 md:block"></span>
                  <p className="text-gray-500">
                    {format(diploma.date, 'MMM yyyy')}
                    {!isEmpty(diploma.description) && <DescriptionModal description={diploma.description} />}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-12 space-y-5 pt-12">
            <SectionTitle title={t('pages.about.services')!} />

            <div className="grid grid-cols-1 gap-12 pt-5 text-center md:grid-cols-3">
              <div>
                <div>
                  <i className="icon icon-website mx-auto block h-12 w-12 bg-sky-500" />
                </div>
                <p className="mb-1 mt-3 block font-rubik text-lg font-bold">
                  {t('pages.about.servicesDetails.website.title')}
                </p>
                <p className="text-justify">{t('pages.about.servicesDetails.website.description')}</p>
              </div>

              <div>
                <div>
                  <i className="icon icon-desktop mx-auto block h-12 w-12 bg-gray-500 dark:bg-white" />
                </div>
                <p className="mb-1 mt-3 block font-rubik text-lg font-bold">
                  {t('pages.about.servicesDetails.application.title')}
                </p>
                <p className="text-justify">{t('pages.about.servicesDetails.application.description')}</p>
              </div>

              <div>
                <div>
                  <i className="brand icon-discord mx-auto block h-12 w-12 bg-discord" />
                </div>
                <p className="mb-1 mt-3 block font-rubik text-lg font-bold">
                  {t('pages.about.servicesDetails.discord.title')}
                </p>
                <p className="text-justify">{t('pages.about.servicesDetails.discord.description')}</p>
              </div>
            </div>

            <div
              className="relative !mt-16 h-96 rounded-3xl"
              style={{ background: "url('/imgs/bg-pro.jpg') center center", backgroundClip: 'content-box' }}
              id="contact"
            >
              <div className="sticky z-50 flex h-96 items-center justify-center text-center">
                <div className="max-w-md">
                  <h1 className="mb-5 font-rubik text-5xl font-bold text-white">
                    {t('pages.about.servicesHero.title')}
                  </h1>
                  <p className="mb-5 text-white">{t('pages.about.servicesHero.description')}</p>

                  <a
                    target="_blank"
                    href="mailto:contact@qlaffont.com"
                    rel="noreferrer"
                    className="mx-auto inline-block w-full md:w-auto"
                    onClick={() => posthog.capture('contact_email_clicked', { page: 'about' })}
                  >
                    <div className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1c1b22] px-2 py-3 font-medium text-white shadow-lg shadow-[#1c1b22]/20 hover:opacity-70 dark:bg-gray-200 dark:text-[#1c1b22] dark:shadow-gray-200/20 md:w-80 md:px-12">
                      <span className="icon icon-mail block h-4 w-4 bg-white dark:bg-black"></span>{' '}
                      <span>contact@qlaffont.com</span>
                    </div>
                  </a>
                  <a
                    target="_blank"
                    href="tel:+33629869841"
                    rel="noreferrer"
                    className="mx-auto inline-block w-full md:w-auto"
                    onClick={() => posthog.capture('contact_phone_clicked')}
                  >
                    <div className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#1c1b22] px-2 py-3 font-medium text-white shadow-lg shadow-[#1c1b22]/20 hover:opacity-70 dark:bg-gray-200 dark:text-[#1c1b22] dark:shadow-gray-200/20 md:w-80 md:px-12">
                      <span className="icon icon-phone block h-4 w-4 bg-white dark:bg-black"></span>{' '}
                      <span>+33 6 29 86 98 41</span>
                    </div>
                  </a>
                </div>
              </div>
              <div className="absolute left-0 top-0 z-0 h-96 w-full rounded-3xl bg-black opacity-60"></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
